import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listData, updateData } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { CheckCircle, Circle, CircleNotch } from "@phosphor-icons/react";

const NEXT = { todo: "in_progress", in_progress: "done", done: "todo" };
const ICON = { todo: Circle, in_progress: CircleNotch, done: CheckCircle };

export default function PlanPage() {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const key = ["data", "plan_tasks", activeId];
  const { data: tasks = [] } = useQuery({ queryKey: key, queryFn: () => listData("plan_tasks", activeId), enabled: !!activeId });

  const m = useMutation({
    mutationFn: ({ id, status }) => updateData("plan_tasks", id, { status }),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData(key);
      qc.setQueryData(key, (old) => old.map((t) => (t.id === id ? { ...t, status } : t)));
      return { prev };
    },
    onError: (_e, _v, ctx) => qc.setQueryData(key, ctx.prev),
    onSettled: () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["dashboard", activeId] }); },
  });

  const phases = [...new Set(tasks.map((t) => t.phase))];
  const done = tasks.filter((t) => t.status === "done").length;

  return (
    <PageContainer>
      <PageHeader eyebrow="Execution" title="30-Day Marketing Plan"
        subtitle="The step-by-step growth roadmap. Click a task's status icon to advance it: To do → In progress → Done.">
        <div className="text-right"><div className="font-head text-2xl brand-text">{done}/{tasks.length}</div><div className="label-caps">completed</div></div>
      </PageHeader>

      <div className="space-y-6">
        {phases.map((phase) => {
          const items = tasks.filter((t) => t.phase === phase);
          return (
            <div key={phase}>
              <div className="flex items-center gap-3 mb-3">
                <h3 className="font-head text-base font-semibold">{phase}</h3>
                <div className="h-px flex-1 bg-white/8" />
                <span className="text-xs text-[#7a7a7a]">{items.filter((i) => i.status === "done").length}/{items.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {items.map((t) => {
                  const Icon = ICON[t.status] || Circle;
                  return (
                    <div key={t.id} className="card-matte p-4 flex items-start gap-3" data-testid="plan-task">
                      <button data-testid="plan-task-toggle" onClick={() => m.mutate({ id: t.id, status: NEXT[t.status] })}
                        className={`mt-0.5 ${t.status === "done" ? "text-emerald-400" : t.status === "in_progress" ? "brand-text" : "text-[#5a5a5a] hover:text-white"}`}>
                        <Icon size={22} weight={t.status === "todo" ? "regular" : "fill"} />
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`font-semibold text-sm ${t.status === "done" ? "line-through text-[#7a7a7a]" : ""}`}>
                            <span className="brand-text mr-1.5">Day {t.day}</span>{t.title}
                          </span>
                          <StatusBadge value={t.status} />
                        </div>
                        <div className="text-sm text-[#9a9a9a] mt-1">{t.description}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#5a5a5a] mt-2">{t.category}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </PageContainer>
  );
}
